import type {Config as HoneepotConfig} from '@/modules/common/context/Honeepot/models';

import createCache from '@emotion/cache';
import {CacheProvider} from '@emotion/react';
import {QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {ConfigurationProvider, BreakpointProvider} from '@travel-frontend/core';
import {ThemeProvider, themeTokens} from '@travel-frontend/themes';
import Cookies from 'js-cookie';
import {I18nextProvider} from 'react-i18next';
import {Provider as ReduxStoreProvider} from 'react-redux';

// eslint-disable-next-line import/extensions -- expected
import patchedHoneepotSdkUrlForDevMode from '@/assets/scripts/odysseus.js?url';
import {LayerPortal} from '@/components/LayerPortal';
import i18n from '@/i18n';
import {PostHogProvider} from '@/modules/common/components/PostHogProvider';
import {BloomreachProvider} from '@/modules/common/context/Bloomreach';
import {ConfigProvider} from '@/modules/common/context/Config';
import {ConsentProvider} from '@/modules/common/context/Consent';
import {HoneepotProvider} from '@/modules/common/context/Honeepot';
import {PreviousPageProvider} from '@/modules/common/context/PreviousPage';
import {QueryParamsProvider} from '@/modules/common/context/QueryParams';
import {queryClient} from '@/queryClient';
import {reduxStore} from '@/redux/config';
import {createDirectusAssetUrl} from '@/utils/createDirectusAssetUrl';

type Props = React.PropsWithChildren;

const FindEmotionCache = createCache({
  key: 'find',
});

/**
 * The honeepot sdk does not work on localhost because it does an origin check.
 * And for some reason they can't configure something like "localhost*" in their list of allowed domains...
 * To get around this we need to copy the staging (or production, see .env) sdk and patch a method.
 *
 * 1. Search for the definition of the "initiatorCheck()"-method
 * 2. At the beginning, change:
 *    - this._initiatorUrl.value = window.location.href;
 *    + this._initiatorUrl.value = 'https://aidu-de.test.invia-travel.de/';
 */
const honeePotSdkUrl =
  import.meta.env.MODE === 'development'
    ? patchedHoneepotSdkUrlForDevMode
    : import.meta.env.VITE_HONEEPOT_SDK_URL;

const honeePotConfig: HoneepotConfig = {
  desktop: {
    hideBannerFlagOnFirstVisit: true,
    idleTimeout: 0,
    onMouseLeave: false,
    onTabInactive: false,
  },
  mobile: {
    hideBannerFlagOnFirstVisit: false,
    idleTimeout: 0,
    onMouseLeave: false,
    onTabInactive: false,
  },
};

// this will suppress the following error
// The pseudo class "${unsafePseudoClass}" is potentially unsafe when doing server-side rendering...
FindEmotionCache.compat = true;

export const GlobalProviders: React.FunctionComponent<Props> = ({children}) => {
  return (
    <PostHogProvider>
      <CacheProvider value={FindEmotionCache}>
        <QueryClientProvider client={queryClient}>
          <LayerPortal>
            <ReactQueryDevtools
              initialIsOpen={false}
              buttonPosition="bottom-left"
            />
          </LayerPortal>
          <QueryParamsProvider>
            <ConfigProvider>
              <I18nextProvider i18n={i18n}>
                <ThemeProvider
                  themeTokens={themeTokens[import.meta.env.VITE_PORTAL]}
                  isAccessibleTheme={
                    Cookies.get('isAccessibleThemeEnabled') === 'true'
                  }
                >
                  <ConfigurationProvider
                    value={{
                      brandName: import.meta.env.VITE_BRAND_NAME,
                      portal: import.meta.env.VITE_PORTAL,
                      authUrl: import.meta.env.VITE_SERVICE_AUTH_URL,
                      domain: import.meta.env.VITE_PORTAL_DOMAIN,
                      linkMaskingKey: '',
                      newsletterApiKey: import.meta.env
                        .VITE_SERVICE_NEWSLETTER_KEY,
                      newsletterSubscribeUrl: import.meta.env
                        .VITE_SERVICE_NEWSLETTER_URL,
                      origin: import.meta.env.VITE_PORTAL_ORIGIN,
                      useNextImage: false,
                      upliftAds: {
                        apiUrl: import.meta.env.VITE_UPLIFT_API_URL,
                        apiKey: import.meta.env.VITE_UPLIFT_API_KEY,
                        siteId: import.meta.env.VITE_UPLIFT_SITE_ID,
                      },
                      tld: import.meta.env.VITE_PORTAL_TLD,
                      directusImageUrl: createDirectusAssetUrl(''),
                      loginUrl: import.meta.env.VITE_LOGIN_URL,
                      wishlistUrl: import.meta.env.VITE_WISHLIST_URL,
                      wishlistBaseUrl: import.meta.env
                        .VITE_SERVICE_WISHLIST_URL,
                      reviewServiceUrl: import.meta.env
                        .VITE_SERVICE_REVIEW_SERVICE_URL,
                      hotelDetailsUrl: import.meta.env
                        .VITE_SERVICE_HOTEL_DETAILS_URL,
                      googleMapsKey: import.meta.env
                        .VITE_SERVICE_GOOGLE_MAPS_KEY,
                    }}
                  >
                    <BreakpointProvider isClientSideOnlyApp>
                      <ReduxStoreProvider store={reduxStore}>
                        <ConsentProvider>
                          <BloomreachProvider>
                            <HoneepotProvider
                              sdkUrl={honeePotSdkUrl}
                              config={honeePotConfig}
                            >
                              <PreviousPageProvider>
                                {children}
                              </PreviousPageProvider>
                            </HoneepotProvider>
                          </BloomreachProvider>
                        </ConsentProvider>
                      </ReduxStoreProvider>
                    </BreakpointProvider>
                  </ConfigurationProvider>
                </ThemeProvider>
              </I18nextProvider>
            </ConfigProvider>
          </QueryParamsProvider>
        </QueryClientProvider>
      </CacheProvider>
    </PostHogProvider>
  );
};
